import {
  EmissionFactor,
  UpsertEmissionFactorRequest,
  SustainabilityReport
} from '../types/emission';
import { ApiResponse } from '../types/transportation';
import { getToken } from '../utils/auth';

const _configured = (import.meta as any).env?.VITE_API_BASE as string | undefined;
const API_BASE_URL = _configured && /^https?:\/\//i.test(_configured) ? _configured : window.location.origin;

class EmissionApiService {
  private authHeaders(): Record<string, string> {
    const token = getToken();
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` })
    };
  }

  private async fetchJson<T>(url: string, options: RequestInit = {}): Promise<T> {
    const response = await fetch(`${API_BASE_URL}/api${url}`, {
      ...options,
      headers: { ...this.authHeaders(), ...options.headers }
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  // ── Emission factors ─────────────────────────────────────────────
  async getAll(): Promise<EmissionFactor[]> {
    const result = await this.fetchJson<ApiResponse<EmissionFactor[]>>(`/EmissionFactor`);
    if (result.success && result.data) return result.data;
    throw new Error(result.message || 'Failed to fetch emission factors');
  }

  async upsert(vehicleType: string, request: UpsertEmissionFactorRequest): Promise<EmissionFactor> {
    const result = await this.fetchJson<ApiResponse<EmissionFactor>>(`/EmissionFactor/${vehicleType}`, {
      method: 'PUT',
      body: JSON.stringify(request)
    });
    if (result.success && result.data) return result.data;
    throw new Error(result.message || 'Failed to save emission factor');
  }

  // ── Sustainability report & export ───────────────────────────────
  async getReport(from: string, to: string): Promise<SustainabilityReport> {
    const result = await this.fetchJson<ApiResponse<SustainabilityReport>>(
      `/Sustainability/report?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`
    );
    if (result.success && result.data) return result.data;
    throw new Error(result.message || 'Failed to fetch sustainability report');
  }

  async downloadExport(from: string, to: string, format: 'csv' | 'json' | 'ecocockpit'): Promise<void> {
    const response = await fetch(
      `${API_BASE_URL}/api/Sustainability/export?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&format=${format}`,
      { headers: this.authHeaders() }
    );
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

    const blob = await response.blob();
    const disposition = response.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename="?([^"]+)"?/i);
    const fileName = match ? match[1] : `sustainability_${from}_${to}.${format === 'json' ? 'json' : 'csv'}`;

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }
}

export const emissionApi = new EmissionApiService();
